import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { subMonths, startOfMonth, endOfMonth, format, eachMonthOfInterval } from 'date-fns';

export interface DashboardFilters {
  customerIds?: string[];
  vendorIds?: string[];
  accountIds?: string[];
  siteIds?: string[];
  dateRange?: {
    from: Date;
    to: Date;
  };
}

export interface UtilityMetrics {
  electricity: {
    totalKwh: number;
    totalSpend: number;
    sitesCount: number;
    accountsCount: number;
  };
  gas: {
    totalTherms: number;
    totalSpend: number;
    sitesCount: number;
    accountsCount: number;
  };
  water: {
    totalGallons: number;
    totalSpend: number;
    sitesCount: number;
    accountsCount: number;
  };
}

export interface SiteSpendComparison {
  siteId: string;
  siteName: string;
  utilities: string[];
  currentPeriodSpend: number;
  previousPeriodSpend: number;
  variance: number;
  variancePercent: number;
}

export interface MonthlySpendData {
  month: string;
  electricity: number;
  gas: number;
  water: number;
  total: number;
}

export interface UtilityPerformanceData {
  month: string;
  usage: number;
  spend: number;
  emissions?: number;
}

export interface SiteUsageComparison {
  siteId: string;
  siteName: string;
  usage: number;
  spend: number;
  unitCost: number;
}

export interface DashboardKPIs {
  totalSpend: number;
  totalEmissions: number;
  totalSites: number;
}

export interface LocationData {
  siteId: string;
  siteName: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  totalSpend: number;
  utilityTypes: string[];
  siteCount?: number;
}

export interface SmartMetric {
  id: string;
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'increase' | 'decrease' | 'neutral';
  description: string;
  icon: string;
  trend?: 'up' | 'down' | 'stable';
}

export interface LocationInsight {
  region: string;
  siteCount: number;
  totalSpend: number;
  avgSpendPerSite: number;
  topUtility: string;
}

export const useTumDashboard = (filters: DashboardFilters = {}) => {
  const { currentWorkspace } = useWorkspace();
  
  // Default to last 6 months if no date range provided
  // Ensure dates are valid and not in the future
  const now = new Date();
  const defaultFrom = startOfMonth(subMonths(now, 6));
  const defaultTo = endOfMonth(now);
  
  const dateRange = filters.dateRange || {
    from: defaultFrom,
    to: defaultTo,
  };

  // Validate date range - ensure 'to' is not before 'from' and not in the future
  const validDateRange = {
    from: dateRange.from > now ? defaultFrom : dateRange.from,
    to: dateRange.to > now ? defaultTo : (dateRange.to < dateRange.from ? defaultTo : dateRange.to),
  };

  // Fetch dashboard data
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['tum-dashboard', currentWorkspace?.id, filters, validDateRange],
    queryFn: async () => {
      if (!currentWorkspace?.id) throw new Error('Workspace not available');

      // Format dates safely
      const fromDate = format(validDateRange.from, 'yyyy-MM-dd');
      const toDate = format(validDateRange.to, 'yyyy-MM-dd');

      // Build base query with filters
      let billsQuery = supabase
        .from('bills')
        .select(`
          id,
          amount,
          bill_date,
          tum_property_id,
          tum_utility_account_id,
          line_items
        `)
        .eq('workspace_id', currentWorkspace.id)
        .gte('bill_date', fromDate)
        .lte('bill_date', toDate);

      // Apply filters
      if (filters.accountIds && filters.accountIds.length > 0) {
        billsQuery = billsQuery.in('tum_utility_account_id', filters.accountIds);
      }
      if (filters.siteIds && filters.siteIds.length > 0) {
        billsQuery = billsQuery.in('tum_property_id', filters.siteIds);
      }

      const { data: bills, error: billsError } = await billsQuery;

      if (billsError) {
        console.error('Error fetching bills:', billsError);
        // Return empty data structure instead of throwing
        // This allows the component to show sample data
        return {
          kpis: { totalSpend: 0, totalEmissions: 0, totalSites: 0 },
          utilityMetrics: {
            electricity: { totalKwh: 0, totalSpend: 0, sitesCount: 0, accountsCount: 0 },
            gas: { totalTherms: 0, totalSpend: 0, sitesCount: 0, accountsCount: 0 },
            water: { totalGallons: 0, totalSpend: 0, sitesCount: 0, accountsCount: 0 },
          },
          spendComparison: [],
          monthlySpend: [],
          electricityPerformance: [],
          gasPerformance: [],
          waterPerformance: [],
          electricityUsageComparison: [],
          gasUsageComparison: [],
          waterUsageComparison: [],
        };
      }

      // Fetch related data separately for better performance
      const accountIds = [...new Set((bills || []).map((b: any) => b.tum_utility_account_id).filter(Boolean))];
      const propertyIds = [...new Set((bills || []).map((b: any) => b.tum_property_id).filter(Boolean))];

      // Fetch utility accounts with related data
      let accountsQuery = supabase
        .from('tum_utility_accounts')
        .select(`
          id,
          commodity,
          account_number,
          property_id,
          vendor_id
        `)
        .eq('workspace_id', currentWorkspace.id);

      if (accountIds.length > 0) {
        accountsQuery = accountsQuery.in('id', accountIds);
      }

      // Apply vendor filter
      if (filters.vendorIds && filters.vendorIds.length > 0) {
        accountsQuery = accountsQuery.in('vendor_id', filters.vendorIds);
      }

      const { data: accounts } = await accountsQuery;

      // Fetch properties with full address data
      let propertiesQuery = supabase
        .from('tum_properties')
        .select(`
          id,
          property_name,
          address,
          customer_id,
          property_type,
          total_units
        `)
        .eq('workspace_id', currentWorkspace.id);

      if (propertyIds.length > 0) {
        propertiesQuery = propertiesQuery.in('id', propertyIds);
      }

      // Apply customer filter
      if (filters.customerIds && filters.customerIds.length > 0) {
        propertiesQuery = propertiesQuery.in('customer_id', filters.customerIds);
      }

      const { data: properties } = await propertiesQuery;

      // Fetch vendors
      const vendorIds = [...new Set((accounts || []).map((a: any) => a.vendor_id).filter(Boolean))];
      const { data: vendors } = vendorIds.length > 0 ? await supabase
        .from('tum_utility_vendors')
        .select('id, vendor_name')
        .eq('workspace_id', currentWorkspace.id)
        .in('id', vendorIds) : { data: [] };

      // Create lookup maps
      const accountMap = new Map((accounts || []).map((a: any) => [a.id, a]));
      const propertyMap = new Map((properties || []).map((p: any) => [p.id, p]));
      const vendorMap = new Map((vendors || []).map((v: any) => [v.id, v]));

      // Calculate previous period for comparison
      const periodMonths = Math.ceil((validDateRange.to.getTime() - validDateRange.from.getTime()) / (1000 * 60 * 60 * 24 * 30));
      const previousPeriodFrom = startOfMonth(subMonths(validDateRange.from, periodMonths));
      const previousPeriodTo = endOfMonth(subMonths(validDateRange.to, periodMonths));

      let previousBillsQuery = supabase
        .from('bills')
        .select('id, amount, bill_date, tum_property_id, tum_utility_account_id')
        .eq('workspace_id', currentWorkspace.id)
        .gte('bill_date', format(previousPeriodFrom, 'yyyy-MM-dd'))
        .lte('bill_date', format(previousPeriodTo, 'yyyy-MM-dd'));

      const { data: previousBillsData } = await previousBillsQuery;
      
      let previousBills = previousBillsData || [];
      
      if (previousBills.length > 0 && filters.customerIds && filters.customerIds.length > 0) {
        // Filter previous bills by same criteria if needed
        const previousPropertyIds = [...new Set(previousBills.map((b: any) => b.tum_property_id).filter(Boolean))];
        if (previousPropertyIds.length > 0) {
          const { data: previousProperties } = await supabase
            .from('tum_properties')
            .select('id, customer_id')
            .eq('workspace_id', currentWorkspace.id)
            .in('id', previousPropertyIds);

          if (previousProperties) {
            const validPropertyIds = new Set(
              previousProperties
                .filter((p: any) => filters.customerIds!.includes(p.customer_id))
                .map((p: any) => p.id)
            );
            // Filter previous bills
            previousBills = previousBills.filter((b: any) => 
              !b.tum_property_id || validPropertyIds.has(b.tum_property_id)
            );
          }
        }
      }

      // Process bills data with joined information
      const processedBills = (bills || []).map((bill: any) => {
        const account = accountMap.get(bill.tum_utility_account_id);
        const property = propertyMap.get(bill.tum_property_id);
        const vendor = account ? vendorMap.get(account.vendor_id) : null;

        // Filter out bills that don't match customer filter if applied
        if (filters.customerIds && filters.customerIds.length > 0) {
          if (!property || !filters.customerIds.includes(property.customer_id)) {
            return null;
          }
        }

        return {
          ...bill,
          commodity: account?.commodity?.toLowerCase() || 'other',
          propertyName: property?.property_name || 'Unknown',
          propertyId: property?.id || bill.tum_property_id,
          vendorName: vendor?.vendor_name || 'Unknown',
          accountNumber: account?.account_number || '',
        };
      }).filter(Boolean);

      // Calculate utility metrics
      const utilityMetrics: UtilityMetrics = {
        electricity: {
          totalKwh: 0,
          totalSpend: 0,
          sitesCount: 0,
          accountsCount: 0,
        },
        gas: {
          totalTherms: 0,
          totalSpend: 0,
          sitesCount: 0,
          accountsCount: 0,
        },
        water: {
          totalGallons: 0,
          totalSpend: 0,
          sitesCount: 0,
          accountsCount: 0,
        },
      };

      const siteSpendMap = new Map<string, {
        siteName: string;
        utilities: Set<string>;
        currentSpend: number;
        previousSpend: number;
      }>();

      const monthlySpendMap = new Map<string, {
        electricity: number;
        gas: number;
        water: number;
      }>();

      const utilityPerformanceMap = new Map<string, Map<string, {
        usage: number;
        spend: number;
      }>>();

      const siteUsageMap = new Map<string, Map<string, {
        usage: number;
        spend: number;
      }>>();

      const uniqueSites = new Set<string>();
      const uniqueAccounts = new Set<string>();
      const uniqueSitesByUtility = {
        electricity: new Set<string>(),
        gas: new Set<string>(),
        water: new Set<string>(),
      };
      const uniqueAccountsByUtility = {
        electricity: new Set<string>(),
        gas: new Set<string>(),
        water: new Set<string>(),
      };

      // Process current period bills
      processedBills.forEach((bill: any) => {
        const commodity = bill.commodity;
        const month = format(new Date(bill.bill_date), 'yyyy-MM');
        const siteId = bill.propertyId;
        const siteName = bill.propertyName;
        const amount = parseFloat(bill.amount) || 0;

        // Update site spend
        if (siteId) {
          if (!siteSpendMap.has(siteId)) {
            siteSpendMap.set(siteId, {
              siteName,
              utilities: new Set(),
              currentSpend: 0,
              previousSpend: 0,
            });
          }
          const siteData = siteSpendMap.get(siteId)!;
          siteData.utilities.add(commodity);
          siteData.currentSpend += amount;
          uniqueSites.add(siteId);
        }

        // Update monthly spend
        if (!monthlySpendMap.has(month)) {
          monthlySpendMap.set(month, { electricity: 0, gas: 0, water: 0 });
        }
        const monthlyData = monthlySpendMap.get(month)!;
        if (commodity === 'electricity') monthlyData.electricity += amount;
        else if (commodity === 'gas') monthlyData.gas += amount;
        else if (commodity === 'water') monthlyData.water += amount;

        // Update utility metrics
        if (commodity === 'electricity') {
          utilityMetrics.electricity.totalSpend += amount;
          if (siteId) uniqueSitesByUtility.electricity.add(siteId);
          if (bill.tum_utility_account_id) {
            uniqueAccountsByUtility.electricity.add(bill.tum_utility_account_id);
            uniqueAccounts.add(bill.tum_utility_account_id);
          }
          // Extract usage from line items if available
          if (bill.line_items && Array.isArray(bill.line_items)) {
            const usageItem = bill.line_items.find((item: any) => 
              item.type === 'usage' || item.description?.toLowerCase().includes('kwh')
            );
            if (usageItem) {
              utilityMetrics.electricity.totalKwh += parseFloat(usageItem.quantity || usageItem.amount || 0);
            }
          }
        } else if (commodity === 'gas') {
          utilityMetrics.gas.totalSpend += amount;
          if (siteId) uniqueSitesByUtility.gas.add(siteId);
          if (bill.tum_utility_account_id) {
            uniqueAccountsByUtility.gas.add(bill.tum_utility_account_id);
            uniqueAccounts.add(bill.tum_utility_account_id);
          }
          if (bill.line_items && Array.isArray(bill.line_items)) {
            const usageItem = bill.line_items.find((item: any) => 
              item.type === 'usage' || item.description?.toLowerCase().includes('therm')
            );
            if (usageItem) {
              utilityMetrics.gas.totalTherms += parseFloat(usageItem.quantity || usageItem.amount || 0);
            }
          }
        } else if (commodity === 'water') {
          utilityMetrics.water.totalSpend += amount;
          if (siteId) uniqueSitesByUtility.water.add(siteId);
          if (bill.tum_utility_account_id) {
            uniqueAccountsByUtility.water.add(bill.tum_utility_account_id);
            uniqueAccounts.add(bill.tum_utility_account_id);
          }
          if (bill.line_items && Array.isArray(bill.line_items)) {
            const usageItem = bill.line_items.find((item: any) => 
              item.type === 'usage' || item.description?.toLowerCase().includes('gallon')
            );
            if (usageItem) {
              utilityMetrics.water.totalGallons += parseFloat(usageItem.quantity || usageItem.amount || 0);
            }
          }
        }

        // Update utility performance
        if (!utilityPerformanceMap.has(commodity)) {
          utilityPerformanceMap.set(commodity, new Map());
        }
        const perfMap = utilityPerformanceMap.get(commodity)!;
        if (!perfMap.has(month)) {
          perfMap.set(month, { usage: 0, spend: 0 });
        }
        const perfData = perfMap.get(month)!;
        perfData.spend += amount;
        // Usage would be extracted from line items

        // Update site usage comparison
        if (siteId) {
          if (!siteUsageMap.has(commodity)) {
            siteUsageMap.set(commodity, new Map());
          }
          const siteUsage = siteUsageMap.get(commodity)!;
          if (!siteUsage.has(siteId)) {
            siteUsage.set(siteId, { usage: 0, spend: 0, siteName });
          }
          const siteData = siteUsage.get(siteId)!;
          siteData.spend += amount;
        }
      });

      // Process previous period bills
      (previousBills || []).forEach((bill: any) => {
        const siteId = bill.tum_property_id;
        if (siteId && siteSpendMap.has(siteId)) {
          const siteData = siteSpendMap.get(siteId)!;
          siteData.previousSpend += parseFloat(bill.amount) || 0;
        }
      });

      // Calculate final metrics
      utilityMetrics.electricity.sitesCount = uniqueSitesByUtility.electricity.size;
      utilityMetrics.electricity.accountsCount = uniqueAccountsByUtility.electricity.size;
      utilityMetrics.gas.sitesCount = uniqueSitesByUtility.gas.size;
      utilityMetrics.gas.accountsCount = uniqueAccountsByUtility.gas.size;
      utilityMetrics.water.sitesCount = uniqueSitesByUtility.water.size;
      utilityMetrics.water.accountsCount = uniqueAccountsByUtility.water.size;

      // Build spend comparison
      const spendComparison: SiteSpendComparison[] = Array.from(siteSpendMap.entries()).map(([siteId, data]) => {
        const variance = data.currentSpend - data.previousSpend;
        const variancePercent = data.previousSpend > 0 
          ? (variance / data.previousSpend) * 100 
          : 0;
        return {
          siteId,
          siteName: data.siteName,
          utilities: Array.from(data.utilities),
          currentPeriodSpend: data.currentSpend,
          previousPeriodSpend: data.previousSpend,
          variance,
          variancePercent,
        };
      }).sort((a, b) => b.currentPeriodSpend - a.currentPeriodSpend);

      // Build monthly spend data
      const months = eachMonthOfInterval({ start: dateRange.from, end: dateRange.to });
      const monthlySpend: MonthlySpendData[] = months.map(month => {
        const monthKey = format(month, 'yyyy-MM');
        const data = monthlySpendMap.get(monthKey) || { electricity: 0, gas: 0, water: 0 };
        return {
          month: format(month, 'MMM yyyy'),
          ...data,
          total: data.electricity + data.gas + data.water,
        };
      });

      // Build utility performance data
      const electricityPerformance: UtilityPerformanceData[] = months.map(month => {
        const monthKey = format(month, 'yyyy-MM');
        const data = utilityPerformanceMap.get('electricity')?.get(monthKey) || { usage: 0, spend: 0 };
        return {
          month: format(month, 'MM/dd'),
          ...data,
        };
      });

      const gasPerformance: UtilityPerformanceData[] = months.map(month => {
        const monthKey = format(month, 'yyyy-MM');
        const data = utilityPerformanceMap.get('gas')?.get(monthKey) || { usage: 0, spend: 0 };
        return {
          month: format(month, 'MM/dd'),
          ...data,
        };
      });

      const waterPerformance: UtilityPerformanceData[] = months.map(month => {
        const monthKey = format(month, 'yyyy-MM');
        const data = utilityPerformanceMap.get('water')?.get(monthKey) || { usage: 0, spend: 0 };
        return {
          month: format(month, 'MM/dd'),
          ...data,
        };
      });

      // Build site usage comparisons
      const electricityUsageComparison: SiteUsageComparison[] = Array.from(
        siteUsageMap.get('electricity')?.entries() || []
      ).map(([siteId, data]) => ({
        siteId,
        siteName: data.siteName,
        usage: data.usage,
        spend: data.spend,
        unitCost: data.usage > 0 ? data.spend / data.usage : 0,
      })).sort((a, b) => b.spend - a.spend);

      const gasUsageComparison: SiteUsageComparison[] = Array.from(
        siteUsageMap.get('gas')?.entries() || []
      ).map(([siteId, data]) => ({
        siteId,
        siteName: data.siteName,
        usage: data.usage,
        spend: data.spend,
        unitCost: data.usage > 0 ? data.spend / data.usage : 0,
      })).sort((a, b) => b.spend - a.spend);

      const waterUsageComparison: SiteUsageComparison[] = Array.from(
        siteUsageMap.get('water')?.entries() || []
      ).map(([siteId, data]) => ({
        siteId,
        siteName: data.siteName,
        usage: data.usage,
        spend: data.spend,
        unitCost: data.usage > 0 ? data.spend / data.usage : 0,
      })).sort((a, b) => b.spend - a.spend);

      // Calculate KPIs
      const totalSpend = processedBills.reduce((sum: number, bill: any) => 
        sum + (parseFloat(bill.amount) || 0), 0
      );
      
      // Estimate emissions (simplified - would need actual conversion factors)
      const totalEmissions = utilityMetrics.electricity.totalKwh * 0.0004 + 
                            utilityMetrics.gas.totalTherms * 5.3 +
                            utilityMetrics.water.totalGallons * 0.000001;

      const kpis: DashboardKPIs = {
        totalSpend,
        totalEmissions,
        totalSites: uniqueSites.size,
      };

      // Build location data with coordinates
      const locationData: LocationData[] = Array.from(siteSpendMap.entries()).map(([siteId, data]) => {
        const property = propertyMap.get(siteId);
        const address = property?.address as any;
        
        // Extract coordinates from address JSONB or use geocoding fallback
        let latitude: number | undefined;
        let longitude: number | undefined;
        
        if (address) {
          if (address.latitude && address.longitude) {
            latitude = parseFloat(address.latitude);
            longitude = parseFloat(address.longitude);
          } else if (address.coordinates) {
            latitude = parseFloat(address.coordinates.lat || address.coordinates.latitude);
            longitude = parseFloat(address.coordinates.lng || address.coordinates.longitude);
          }
        }

        // Generate sample coordinates if not available (for demo purposes)
        if (!latitude || !longitude) {
          // Use a hash of the site ID to generate consistent pseudo-coordinates
          const hash = siteId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
          latitude = 39.8283 + (hash % 100) / 1000; // US center latitude with variation
          longitude = -98.5795 + (hash % 100) / 1000; // US center longitude with variation
        }

        return {
          siteId,
          siteName: data.siteName,
          latitude,
          longitude,
          address: address?.street || address?.address_line_1 || '',
          city: address?.city || '',
          state: address?.state || address?.state_province || '',
          zipCode: address?.zip_code || address?.postal_code || '',
          totalSpend: data.currentSpend,
          utilityTypes: Array.from(data.utilities),
        };
      });

      // Calculate location insights by region
      const locationInsights: LocationInsight[] = [];
      const regionMap = new Map<string, {
        sites: Set<string>;
        spend: number;
        utilities: Map<string, number>;
      }>();

      locationData.forEach(loc => {
        const region = loc.state || 'Unknown';
        if (!regionMap.has(region)) {
          regionMap.set(region, {
            sites: new Set(),
            spend: 0,
            utilities: new Map(),
          });
        }
        const regionData = regionMap.get(region)!;
        regionData.sites.add(loc.siteId);
        regionData.spend += loc.totalSpend;
        loc.utilityTypes.forEach(util => {
          regionData.utilities.set(util, (regionData.utilities.get(util) || 0) + 1);
        });
      });

      Array.from(regionMap.entries()).forEach(([region, data]) => {
        const topUtility = Array.from(data.utilities.entries())
          .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
        
        locationInsights.push({
          region,
          siteCount: data.sites.size,
          totalSpend: data.spend,
          avgSpendPerSite: data.sites.size > 0 ? data.spend / data.sites.size : 0,
          topUtility,
        });
      });

      // Calculate smart metrics
      const avgSpendPerSite = uniqueSites.size > 0 ? totalSpend / uniqueSites.size : 0;
      const spendGrowth = spendComparison.length > 0
        ? spendComparison.reduce((sum, item) => sum + item.variancePercent, 0) / spendComparison.length
        : 0;
      
      const topSpendingSite = spendComparison[0];
      const efficiencyScore = utilityMetrics.electricity.totalKwh > 0 && utilityMetrics.electricity.totalSpend > 0
        ? (utilityMetrics.electricity.totalKwh / utilityMetrics.electricity.totalSpend) * 1000
        : 0;

      // Helper function for currency formatting
      const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      };

      const smartMetrics: SmartMetric[] = [
        {
          id: 'avg-spend',
          title: 'Avg Spend per Site',
          value: formatCurrency(avgSpendPerSite),
          change: spendGrowth,
          changeType: spendGrowth > 0 ? 'increase' : spendGrowth < 0 ? 'decrease' : 'neutral',
          description: 'Average utility spend per location',
          icon: 'dollar-sign',
          trend: spendGrowth > 5 ? 'up' : spendGrowth < -5 ? 'down' : 'stable',
        },
        {
          id: 'top-site',
          title: 'Top Spending Site',
          value: topSpendingSite?.siteName || 'N/A',
          description: `Spending ${topSpendingSite ? formatCurrency(topSpendingSite.currentPeriodSpend) : '$0'}`,
          icon: 'building',
        },
        {
          id: 'efficiency',
          title: 'Energy Efficiency',
          value: efficiencyScore > 0 ? `${efficiencyScore.toFixed(1)} kWh/$` : 'N/A',
          description: 'Energy output per dollar spent',
          icon: 'zap',
          trend: efficiencyScore > 10 ? 'up' : 'stable',
        },
        {
          id: 'coverage',
          title: 'Geographic Coverage',
          value: locationInsights.length,
          description: `${locationInsights.length} regions with active sites`,
          icon: 'map-pin',
        },
        {
          id: 'util-diversity',
          title: 'Utility Diversity',
          value: new Set([
            ...utilityMetrics.electricity.sitesCount > 0 ? ['electricity'] : [],
            ...utilityMetrics.gas.sitesCount > 0 ? ['gas'] : [],
            ...utilityMetrics.water.sitesCount > 0 ? ['water'] : [],
          ]).size,
          description: 'Number of utility types in use',
          icon: 'layers',
        },
        {
          id: 'emissions-intensity',
          title: 'Emissions Intensity',
          value: totalSpend > 0 ? `${(totalEmissions / totalSpend * 1000).toFixed(2)} t/$1K` : '0 t/$1K',
          description: 'CO₂e emissions per $1,000 spent',
          icon: 'leaf',
          trend: (totalEmissions / totalSpend) < 10 ? 'down' : 'up',
        },
      ];

      return {
        kpis,
        utilityMetrics,
        spendComparison,
        monthlySpend,
        electricityPerformance,
        gasPerformance,
        waterPerformance,
        electricityUsageComparison,
        gasUsageComparison,
        waterUsageComparison,
        locationData,
        locationInsights,
        smartMetrics,
      };
    },
    enabled: !!currentWorkspace?.id,
    retry: 1,
    retryOnMount: false,
    // Don't throw errors, just return undefined so we can show sample data
    throwOnError: false,
  });

  return {
    data: dashboardData,
    isLoading,
    error,
  };
};

